import React, { useEffect, useMemo, useState } from "react";
import * as Mui from "@mui/material";
import { withStyles } from "tss-react/mui";
import type { WithStyles } from "type/tss";
import MuiAlert from "@mui/material/Alert";
import * as Icons from "react-feather";
import * as Router from "react-router-dom";
import { DragDropContext, Droppable, Draggable, DropResult } from "react-beautiful-dnd";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import { useDispatch, useSelector } from "react-redux";
import { DashboardLayout } from "component/layout/dashboard";
import { PageHeader } from "component/shared/page-header";
import { UnsavedChangesPrompt } from "component/shared/unsaved-changes-prompt";
import { FeatherIcon } from "component/shared/feather-icon";
import { ColdFusionIframe } from "component/shared/iframe/coldfusion";
import { SettingsStickyFooter } from "component/shared/settings-sticky-footer";
import { PageProps } from "shared/page-props";
import { RootState } from "redux/store";
import { getPages } from "redux/selector";
import { fetchPages } from "redux/slice/pages";
import { fetchPropertyTypes, savePropertyTypes, restorePropertyTypes } from "redux/slice/property-types";
import { PropertyTypeItem } from "api/resource/property-types";
import { PageType } from "type/page-type";
import { coldFusionReplatform } from "shared/env";
import { styles } from "./style";

interface Props extends PageProps, Router.RouteComponentProps, WithStyles<typeof styles> {}

const title = "Property Types";

const description =
	"Customize the property type filter in your search. Choose which types to include, " +
	"set their display order, and add custom names. Changes here won't affect how property " +
	"types appear on individual listings.";

const ColdFusionFallback: React.FC<Props> = ({ user }) => (
	<DashboardLayout
		permitted={user && (user.permissions.listingSearch || user.permissions.listingMapSearch)}
		title={title}
		header={
			<Mui.Typography variant="h1">
				<FeatherIcon>
					<Icons.Settings />
				</FeatherIcon>
				{title}
			</Mui.Typography>
		}
		iframe={<ColdFusionIframe url="/propertyTypesPrices.cfm" />}
	/>
);

const SettingsPropertyTypesBase: React.FC<Props> = ({ user, classes = {}, history }) => {
	const dispatch = useDispatch();
	const saved = useSelector((state: RootState) => state.propertyTypes.propertyTypes);
	const loading = useSelector((state: RootState) => state.propertyTypes.loading);
	const pages = useSelector(getPages);

	// Available (board-supported) rows are the editable/reorderable set; unavailable rows are
	// display-only in the accordion. We keep them in separate state so drag order maps 1:1 to
	// the available list.
	const [available, setAvailable] = useState<PropertyTypeItem[]>([]);
	const [unavailable, setUnavailable] = useState<PropertyTypeItem[]>([]);
	// showPriceFields is server-computed and read-only; we echo it back unchanged. Price fields
	// are no longer shown/edited (MAX-3861) — existing price values are sent untouched on save.
	const [showPriceFields, setShowPriceFields] = useState(false);
	// Track which rows the user actually edited the Display Name on, so pre-filling the field
	// with the default `title` never persists that default as a custom override (MAX-3861).
	const [editedNames, setEditedNames] = useState<Set<number>>(new Set());
	const [dirty, setDirty] = useState(false);
	const [submitted, setSubmitted] = useState(false);
	const [saving, setSaving] = useState(false);
	const [restoreOpen, setRestoreOpen] = useState(false);
	const [snackbarOpen, setSnackbarOpen] = useState(false);
	const [snackbarMessage, setSnackbarMessage] = useState("");
	const [snackbarSeverity, setSnackbarSeverity] = useState<"success" | "error">("success");

	useEffect(() => {
		dispatch(fetchPropertyTypes() as any);
		dispatch(fetchPages() as any);
	}, [dispatch]);

	useEffect(() => {
		if (saved) {
			// Sort by sequence rather than trusting API array order — display order is load-bearing
			// here (drag-and-drop reorder, MAX-3861) and the backend is the source of truth. (MAX-3949)
			const all = [...(saved.propertyTypes || [])].sort((a, b) => (a.sequence ?? 0) - (b.sequence ?? 0));
			setAvailable(all.filter((r) => r.boardSupported));
			setUnavailable(all.filter((r) => !r.boardSupported));
			setShowPriceFields(saved.showPriceFields);
			setEditedNames(new Set());
			setDirty(false);
			setSubmitted(false);
		}
	}, [saved]);

	const updateAvailable = (propertyTypeId: number, patch: Partial<PropertyTypeItem>) => {
		setAvailable((prev) => prev.map((r) => (r.propertyTypeId === propertyTypeId ? { ...r, ...patch } : r)));
		setDirty(true);
	};

	const handleNameChange = (propertyTypeId: number, value: string) => {
		setEditedNames((prev) => new Set(prev).add(propertyTypeId));
		updateAvailable(propertyTypeId, { customName: value });
	};

	const onDragEnd = (result: DropResult) => {
		const { destination, source } = result;
		if (!destination || destination.index === source.index) {
			return;
		}
		setAvailable((prev) => {
			const items = Array.from(prev);
			const [moved] = items.splice(source.index, 1);
			items.splice(destination.index, 0, moved);
			return items;
		});
		setDirty(true);
	};

	// Displayed value for the Display Name field: the user's custom name if present, otherwise the
	// pre-filled default (title). Pre-fill is display-only — see buildPayload for the save contract.
	const displayName = (row: PropertyTypeItem) => (row.customName != null ? row.customName : row.title);

	// A blank Display Name only errors for enabled rows (a disabled row's field is grayed out and
	// its value is irrelevant to what shows in search).
	const blankNameIds = useMemo(() => {
		const ids = new Set<number>();
		for (const row of available) {
			if (row.enabled && displayName(row).trim() === "") {
				ids.add(row.propertyTypeId);
			}
		}
		return ids;
	}, [available]);

	const validationError = useMemo(() => {
		if (blankNameIds.size > 0) {
			return "Display name is required.";
		}
		for (const row of available) {
			if (row.customName && row.customName.trim().length > 25) {
				return "Custom name must be 25 characters or fewer.";
			}
		}
		// At least one *available* type must be enabled (unavailable rows can't be toggled).
		if (!available.some((r) => r.enabled)) {
			return "At least one property type must be enabled.";
		}
		return null;
	}, [available, blankNameIds]);

	const notify = (message: string, severity: "success" | "error") => {
		setSnackbarMessage(message);
		setSnackbarSeverity(severity);
		setSnackbarOpen(true);
	};

	// Save contract (MAX-3861):
	// - available rows get a fresh contiguous sequence (1..N) from drag order.
	// - unavailable rows are renumbered into a disjoint range (N+1..) so they never collide with
	//   an available row's sequence (both the client check above and the backend enforce uniqueness
	//   across ALL rows, with no boardSupported exemption).
	// - customName is only overwritten for rows the user actually edited; others send their
	//   originally-loaded value (usually null), so pre-filling with `title` never persists a default.
	// - minPrice/maxPrice are sent unchanged (price fields dropped from the UI, values preserved).
	const buildPayload = (): PropertyTypeItem[] => {
		const availablePayload = available.map((row, index) => ({
			...row,
			sequence: index + 1,
			customName: editedNames.has(row.propertyTypeId)
				? (row.customName && row.customName.trim() !== "" ? row.customName.trim() : null)
				: row.customName,
		}));
		const unavailablePayload = unavailable.map((row, index) => ({
			...row,
			sequence: availablePayload.length + index + 1,
		}));
		return [...availablePayload, ...unavailablePayload];
	};

	const handleSave = async () => {
		setSubmitted(true);
		if (validationError) {
			notify(validationError, "error");
			return;
		}
		setSaving(true);
		try {
			// .unwrap() so a rejected save actually throws — the thunk promise resolves even on
			// failure otherwise, which would falsely report success and disarm the dirty guard.
			await (dispatch(savePropertyTypes({ showPriceFields, propertyTypes: buildPayload() }) as any)).unwrap();
			notify("Property types saved", "success");
			setDirty(false);
			setSubmitted(false);
		} catch(error) {
			// MAX-3930: surface the server's specific validation message (requestLegacy rejects with an
			// Error whose message is the server {message} body) instead of a generic toast.
			const message = error instanceof Error && error.message ? error.message : "Failed to save property types";
			notify(message, "error");
		} finally {
			setSaving(false);
		}
	};

	const handleCancel = () => {
		if (saved) {
			const all = [...(saved.propertyTypes || [])].sort((a, b) => (a.sequence ?? 0) - (b.sequence ?? 0));
			setAvailable(all.filter((r) => r.boardSupported));
			setUnavailable(all.filter((r) => !r.boardSupported));
		}
		setEditedNames(new Set());
		setDirty(false);
		setSubmitted(false);
	};

	const handleRestore = async () => {
		setRestoreOpen(false);
		setSaving(true);
		try {
			await (dispatch(restorePropertyTypes() as any)).unwrap();
			notify("Property types restored to defaults", "success");
			setDirty(false);
			setSubmitted(false);
		} catch{
			notify("Failed to restore defaults", "error");
		} finally {
			setSaving(false);
		}
	};

	// "View Search" — link to the client's live search page (same pattern as settings-locations).
	const listingsPage =
		pages?.find((p) => p.key === "LISTING_SEARCH_FORM")
		?? pages?.find((p) => p.type.id === PageType.LISTINGS.id);
	const viewSearchUrl = listingsPage ? user.baseUrl + listingsPage.path : undefined;

	return (
		<DashboardLayout
			permitted={user && (user.permissions.listingSearch || user.permissions.listingMapSearch)}
			title={title}
			header={
				<PageHeader
					title={title}
					description={description}
					action={
						viewSearchUrl && (
							<Mui.Link href={viewSearchUrl} target="_blank" rel="noopener noreferrer">
								View Search <Icons.ExternalLink size={14} />
							</Mui.Link>
						)
					}
				/>
			}
		>
			<UnsavedChangesPrompt when={dirty} history={history} />

			{loading && <Mui.LinearProgress />}

			<Mui.Box className={classes.restoreRow}>
				<Mui.Link component="button" type="button" className={classes.restoreLink} onClick={() => setRestoreOpen(true)} disabled={saving}>
					Restore Defaults
				</Mui.Link>
			</Mui.Box>

			<div className={classes.table}>
				<div className={classes.headerCells}>
					<div className={classes.colInclude}>Include</div>
					<div className={classes.colType}>Property Type</div>
					<div className={classes.colName}>Display Name</div>
					<div className={classes.colOrder}>Order</div>
				</div>

					<DragDropContext onDragEnd={onDragEnd}>
						<Droppable droppableId="property-types">
							{(provided) => (
								<div ref={provided.innerRef} {...provided.droppableProps}>
									{available.map((row, index) => {
										// Show the blank-name error live once the user has edited (and cleared) a
										// field, or for every blank field after a save attempt.
										const showBlankError =
											blankNameIds.has(row.propertyTypeId) &&
											(submitted || editedNames.has(row.propertyTypeId));
										return (
											<Draggable
												key={row.propertyTypeId}
												draggableId={String(row.propertyTypeId)}
												index={index}
											>
												{(dragProvided) => (
													<div
														ref={dragProvided.innerRef}
														{...dragProvided.draggableProps}
														className={`${classes.row}${!row.enabled ? ` ${classes.rowDisabled}` : ""}${index === available.length - 1 ? ` ${classes.rowLast}` : ""}`}
													>
														<div className={classes.colInclude}>
															<Mui.Checkbox
																className={classes.includeCheckbox}
																disableRipple
																icon={<span className={classes.checkboxIcon} />}
																checkedIcon={
																	<span className={`${classes.checkboxIcon} ${classes.checkboxIconChecked}`}>
																		<Icons.Check size={16} strokeWidth={3} />
																	</span>
																}
																checked={row.enabled}
																onChange={(e) => updateAvailable(row.propertyTypeId, { enabled: e.target.checked })}
															/>
														</div>
														<div className={classes.colType}>
															<Mui.Typography className={classes.typeName}>{row.title}</Mui.Typography>
														</div>
														<div className={classes.colName}>
															<Mui.TextField
																className={classes.nameInput}
																variant="outlined"
																size="small"
																inputProps={{ maxLength: 25 }}
																value={displayName(row)}
																disabled={!row.enabled}
																error={showBlankError}
																helperText={showBlankError ? "Display name is required." : undefined}
																onChange={(e) => handleNameChange(row.propertyTypeId, e.target.value)}
															/>
														</div>
														<div className={classes.colOrder}>
															<span {...dragProvided.dragHandleProps} className={classes.dragHandle}>
																<DragIndicatorIcon />
															</span>
														</div>
													</div>
												)}
											</Draggable>
										);
									})}
									{provided.placeholder}
								</div>
							)}
						</Droppable>
					</DragDropContext>
				</div>

			{unavailable.length > 0 && (
				<Mui.Accordion className={classes.accordion}>
					<Mui.AccordionSummary
						className={classes.accordionSummary}
						expandIcon={<Icons.ChevronDown size={20} />}
						classes={{ content: classes.accordionSummaryContent, expandIconWrapper: classes.accordionExpandIcon }}
					>
						{`Unavailable (${unavailable.length})`}
					</Mui.AccordionSummary>
					<Mui.AccordionDetails className={classes.accordionBody}>
						<Mui.Typography className={classes.accordionDescription}>
							The following property types are unavailable for your account:
						</Mui.Typography>
						<ul className={classes.bulletList}>
							{unavailable.map((row) => (
								<li key={row.propertyTypeId}>{row.title}</li>
							))}
						</ul>
					</Mui.AccordionDetails>
				</Mui.Accordion>
			)}

			<SettingsStickyFooter
				dirty={dirty}
				saving={saving}
				authError={submitted && validationError ? validationError : undefined}
				onSave={handleSave}
				onCancel={handleCancel}
			/>

			<Mui.Dialog open={restoreOpen} onClose={() => setRestoreOpen(false)}>
				<Mui.DialogTitle>Restore Defaults?</Mui.DialogTitle>
				<Mui.DialogContent>
					<Mui.DialogContentText>
						This clears all of your property type customizations (enabled state, custom names, and order) and reverts to the defaults. This cannot be undone.
					</Mui.DialogContentText>
				</Mui.DialogContent>
				<Mui.DialogActions>
					<Mui.Button onClick={() => setRestoreOpen(false)}>Cancel</Mui.Button>
					<Mui.Button color="secondary" onClick={handleRestore}>Restore Defaults</Mui.Button>
				</Mui.DialogActions>
			</Mui.Dialog>

			<Mui.Snackbar open={snackbarOpen} autoHideDuration={4000} onClose={() => setSnackbarOpen(false)}>
				<MuiAlert severity={snackbarSeverity} onClose={() => setSnackbarOpen(false)}>
					{snackbarMessage}
				</MuiAlert>
			</Mui.Snackbar>
		</DashboardLayout>
	);
};

const SettingsPropertyTypes = withStyles(SettingsPropertyTypesBase, styles);

const SettingsPropertyTypesPageBase: React.FC<Props> = (props) => {
	if (!coldFusionReplatform) {
		return <ColdFusionFallback {...props} />;
	}
	return <SettingsPropertyTypes {...props} />;
};

export const SettingsPropertyTypesPage = withStyles(SettingsPropertyTypesPageBase, styles);
